import React, { useState } from 'react';
import CaptchaVerification from './CaptchaVerification';
import Button from '../ui/button';
import { submitFormData, validateWebFormId } from '../../services/tananTechApi';

const ContactForm = ({
  title = "Enquire now",
  webFormId,
  includeSubject = false,
  includeAddress = false,
  includePostcode = false,
  includeMessage = true,
  serviceName = '',
  captchaType = 'plane', // 'plane' or 'house'
  fieldLabels = {},
  submitButtonText = 'Submit',
  inputTextColor = 'text-white',
  onSubmit = null,
  onSuccess = null,
  onError = null,
  className = ""
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    ...(includeSubject && { subject: '' }),
    ...(includeAddress && { address: '' }),
    ...(includePostcode && { postcode: '' }),
    ...(includeMessage && { message: '' }),
    ...(serviceName && { service: serviceName }),
    captcha: ''
  });

  const [selectedCaptcha, setSelectedCaptcha] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCaptchaSelect = (selection) => {
    setSelectedCaptcha(selection);
    setCaptchaError(false);
    setFormData({
      ...formData,
      captcha: selection
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset previous status
    setSubmitStatus(null);
    setSubmitMessage('');
    
    // Check captcha based on type
    const expectedCaptcha = captchaType === 'house' ? 'house' : 'plane';
    if (selectedCaptcha !== expectedCaptcha) {
      setCaptchaError(true);
      return;
    }

    // Validate webFormId
    if (!webFormId) {
      setSubmitStatus('error');
      setSubmitMessage('Form configuration error: missing webFormId');
      return;
    }

    if (!validateWebFormId(webFormId)) {
      setSubmitStatus('error');
      setSubmitMessage('Form configuration error: invalid webFormId format');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Use custom submit handler if provided
      if (onSubmit) {
        await onSubmit(formData, webFormId);
      } else {
        // Default: submit to TananTech API
        const result = await submitFormData(formData, webFormId);
        
        if (result.success) {
          setSubmitStatus('success');
          setSubmitMessage(result.message);
          
          // Reset form on success
          setFormData({
            name: '',
            email: '',
            phone: '',
            ...(includeSubject && { subject: '' }),
            ...(includeAddress && { address: '' }),
            ...(includePostcode && { postcode: '' }),
            ...(includeMessage && { message: '' }),
            ...(serviceName && { service: serviceName }),
            captcha: ''
          });
          setSelectedCaptcha('');
          
          // Call success callback if provided
          if (onSuccess) {
            onSuccess(result);
          }
        } else {
          setSubmitStatus('error');
          setSubmitMessage(result.message);
          
          // Call error callback if provided
          if (onError) {
            onError(result);
          }
        }
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('An unexpected error occurred. Please try again.');
      
      if (onError) {
        onError({ success: false, error: error.message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClassName = `bg-transparent border-2 rounded px-3 sm:px-4 py-2 sm:py-3 ${inputTextColor} placeholder-gray-400 focus:outline-none text-sm sm:text-base`;
  const inputStyle = { borderColor: '#db0058' };

  return (
    <div className={`text-white ${className}`}>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">
        {title.split(' ').map((word, index) => 
          index === title.split(' ').length - 1 ? (
            <span key={index} style={{ color: '#db0058' }}>{word}</span>
          ) : (
            <span key={index}>{word} </span>
          )
        )}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
        <div className="grid grid-cols-1 gap-4">
          <input
            type="text"
            name="name"
            placeholder="Your Name*"
            value={formData.name}
            onChange={handleInputChange}
            className={inputClassName}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = '#b8004a'}
            onBlur={(e) => e.target.style.borderColor = '#db0058'}
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Your Email*"
            value={formData.email}
            onChange={handleInputChange}
            className={inputClassName}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = '#b8004a'}
            onBlur={(e) => e.target.style.borderColor = '#db0058'}
            required
          />
        </div>
        
        <input
          type="tel"
          name="phone"
          placeholder="Phone Number*"
          value={formData.phone}
          onChange={handleInputChange}
          className={`w-full ${inputClassName}`}
          style={inputStyle}
          onFocus={(e) => e.target.style.borderColor = '#b8004a'}
          onBlur={(e) => e.target.style.borderColor = '#db0058'}
          required
        />
        
        {includeAddress && (
          <input
            type="text"
            name="address"
            placeholder={fieldLabels.address || "Your Address*"}
            value={formData.address}
            onChange={handleInputChange}
            className={`w-full ${inputClassName}`}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = '#b8004a'}
            onBlur={(e) => e.target.style.borderColor = '#db0058'}
            required
          />
        )}
        
        {includePostcode && (
          <input
            type="text"
            name="postcode"
            placeholder={fieldLabels.postcode || "Your Postcode*"}
            value={formData.postcode}
            onChange={handleInputChange}
            className={`w-full ${inputClassName}`}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = '#b8004a'}
            onBlur={(e) => e.target.style.borderColor = '#db0058'}
            required
          />
        )}
        
        {includeSubject && (
          <input
            type="text"
            name="subject"
            placeholder="Subject*"
            value={formData.subject}
            onChange={handleInputChange}
            className={`w-full ${inputClassName}`}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = '#b8004a'}
            onBlur={(e) => e.target.style.borderColor = '#db0058'}
            required
          />
        )}
        
        {includeMessage && (
          <textarea
            name="message"
            placeholder="Your Message*"
            value={formData.message}
            onChange={handleInputChange}
            rows="4"
            className={`w-full ${inputClassName} resize-none`}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = '#b8004a'}
            onBlur={(e) => e.target.style.borderColor = '#db0058'}
            required
          ></textarea>
        )}
        
        <CaptchaVerification
          selectedCaptcha={selectedCaptcha}
          onCaptchaSelect={handleCaptchaSelect}
          hasError={captchaError}
          captchaType={captchaType}
        />
        
        {/* Status Messages */}
        {submitStatus && (
          <div className={`p-3 rounded text-sm ${
            submitStatus === 'success'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
            {submitMessage}
          </div>
        )}
        
        <Button
          type="submit"
          variant="primary"
          size="medium"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : submitButtonText}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;